import { useState, useEffect, useRef } from 'react';
import {
  getSeatTypeInfo,
  GRID_SIZE,
  rowIndexToLabel,
  getRowHeaderLabel,
  getSeatNumberForCol,
  displaySeatLabel,
  isSkippedCol,
  isSkippedRow,
  MAX_GRID_COLS,
  MAX_GRID_ROWS,
  MIN_GRID_COLS,
  MIN_GRID_ROWS,
} from '../utils/seatUtils';

export default function Canvas({
  gridRef,
  containerRef,
  seats,
  selectedSeats,
  positioningSeats,
  positioningMode,
  addSeatMode,
  zoom,
  viewOffset,
  isSelecting,
  selectionBox,
  emptyAreaSelection,
  isFullscreen,
  gridCols, // Dinamik grid genişliği
  setGridCols, // Grid genişliğini değiştirme fonksiyonu
  gridRows = 60,
  setGridRows,
  ghostPreviewSeats, // Ghost preview koltukları
  activeTool, // Active tool for header display
  setActiveTool,
  backgroundImage = null,
  backgroundOpacity = 0.85,
  hideToolRail = false,
  hoverCell = null,
  seatTool = 'paint',
  boxStart = null,
  boxEnd = null,
  naming = null,

  // Zone support
  zones = [], // Zone definitions
  useZones = false, // Whether zones are enabled
  selectedZone = null, // Currently selected zone for seat placement
  highlightedZone = null, // Currently highlighted zone in overview
  zoneMode = 'seat-design', // 'zone-overview' | 'zone-edit' | 'seat-design'
  isCreatingZone = false,
  isDrawingZone = false,
  previewZone = null,
  zoneDrawingMode = 'rectangle', // 'rectangle', 'circle', 'polygon'
  polygonPoints = [],
  isPolygonMode = false,
  isDraggingZone = false,
  draggedZone = null,

  // Event handlers
  handleMouseDown,
  handleMouseMove,
  handleMouseUp,
  handleContextMenu,
  handleWheel,
  // Toolbar handlers
  setAddSeatMode,
  undo,
  redo,
  historyIndex,
  history,
  zoomIn,
  zoomOut,
  resetZoom,
  setDirectZoom,
  toggleFullscreen,
  // Tool handlers
  clearSelectedSeats,
  handleZoomClick,
  handlePanStart,
  handlePanMove,
  handlePanEnd,
  setViewOffset,
  onSeatsUpdate,
  onPositionGroup,

  // Zone event handlers
  onZoneClick,
  onZoneDoubleClick,
  onZoneMouseDown,
  onZoneCreateModeChange,
  onZoneExit, // Exit zone edit mode
  showAllZoneSeats = false,
  onToggleShowAllZoneSeats,
  fillEmptyAreaWithSeats,
  setEmptyAreaSelection,
  onAxisContextMenu,
}) {
  // Photoshop-style tool states - activeTool now comes from props
  const [isPanning, setIsPanning] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [spacePan, setSpacePan] = useState(false);
  const previousToolRef = useRef(activeTool);
  const panActiveRef = useRef(false);
  const panStartRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState(null);

  const visibleSeats =
    useZones && zoneMode === 'zone-edit' && selectedZone && !showAllZoneSeats
      ? seats.filter((s) => s.zoneId === selectedZone.id)
      : seats;

  const otherZoneSeatCount =
    useZones && zoneMode === 'zone-edit' && selectedZone
      ? seats.filter((s) => s.zoneId && s.zoneId !== selectedZone.id).length
      : 0;

  // Get tools based on zone mode
  const getTools = () => {
    const baseTools = [
      {
        id: 'select',
        name: 'Seçim Aracı',
        description: 'Koltukları seç ve alanları belirle • Seçili koltukları sürükleyerek taşı',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"
            />
          </svg>
        ),
        active: activeTool === 'select',
        availableIn: ['seat-design', 'zone-edit'],
      },
      {
        id: 'add',
        name: 'Koltuk Ekleme',
        description: 'Yeni koltuklar ekle • Tıkla veya sürükle',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
        ),
        active: activeTool === 'add',
        availableIn: ['seat-design', 'zone-edit'],
      },
      {
        id: 'zone-create',
        name: 'Zone Oluştur',
        description: 'Yeni zone çiz • Canvas üzerinde sürükleyerek zone oluştur',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v8m-4-4h8" />
          </svg>
        ),
        active: activeTool === 'zone-create',
        availableIn: ['zone-overview'],
      },
      {
        id: 'zone-select',
        name: 'Zone Seç/Sürükle',
        description: 'Zone seç ve sürükle • Tek tık: seç • Sürükle: konumu değiştir',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            />
          </svg>
        ),
        active: activeTool === 'zone-select',
        availableIn: ['zone-overview'],
      },
      {
        id: 'zone-transform',
        name: 'Zone Transform',
        description: "Zone boyutunu değiştir • Seçili zone'u yeniden boyutlandır ve şekillendır",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M4 4l6 6m0 0v4.5m0-4.5H6.5"
            />
            <rect x="14" y="14" width="6" height="6" rx="1" />
            <circle cx="9" cy="9" r="1" fill="currentColor" />
            <circle cx="15" cy="15" r="1" fill="currentColor" />
          </svg>
        ),
        active: activeTool === 'zone-transform',
        availableIn: ['zone-overview'],
      },
      {
        id: 'zoom',
        name: 'Büyüteç',
        description: 'Yakınlaştır/Uzaklaştır • Tıkla: Zoom In • Shift+Tıkla: Zoom Out',
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
            <circle cx="11" cy="11" r="8" strokeWidth="2" />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="m21 21-4.35-4.35"
            />
            {isShiftPressed && activeTool === 'zoom' ? (
              // Minus icon for zoom out
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 11h6" />
            ) : (
              // Plus icon for zoom in
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 11h6m-3-3v6"
              />
            )}
          </svg>
        ),
        active: activeTool === 'zoom',
        availableIn: ['seat-design', 'zone-edit', 'zone-overview'],
      },
      {
        id: 'hand',
        name: 'El Aracı',
        description: "Canvas'ı sürükleyerek gezin • Pan ve hareket ettir",
        icon: (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11"
            />
          </svg>
        ),
        active: activeTool === 'hand',
        availableIn: ['seat-design', 'zone-edit', 'zone-overview'],
      },
    ];

    // Filter tools based on current mode
    const currentMode = useZones ? zoneMode : 'seat-design';
    return baseTools.filter((tool) => tool.availableIn.includes(currentMode));
  };

  const tools = getTools();

  // Handle tool selection
  const handleToolSelect = (toolId) => {
    setActiveTool(toolId);

    // Zone-create tool special handling
    if (toolId === 'zone-create' && onZoneCreateModeChange) {
      onZoneCreateModeChange(true);
    } else if (toolId !== 'zone-create' && onZoneCreateModeChange) {
      onZoneCreateModeChange(false);
    }
  };

  // Sync tool state with props
  useEffect(() => {
    if (activeTool === 'add' && !addSeatMode) {
      setAddSeatMode(true);
    } else if (activeTool !== 'add' && addSeatMode) {
      setAddSeatMode(false);
    }
  }, [activeTool, addSeatMode, setAddSeatMode]);

  // Auto-select appropriate tool when mode changes
  useEffect(() => {
    if (useZones && zoneMode === 'zone-overview') {
      // In zone overview, default to zone-select if not already set to a valid tool
      if (!['zone-create', 'zone-select', 'zone-transform', 'zoom', 'hand'].includes(activeTool)) {
        setActiveTool('zone-select');
      }
      // Disable zone creation mode by default
      if (activeTool !== 'zone-create' && onZoneCreateModeChange) {
        onZoneCreateModeChange(false);
      }
    } else if (useZones && zoneMode === 'zone-edit') {
      // In zone edit, default to select if not already set to a valid tool
      if (!['select', 'add', 'zoom', 'hand'].includes(activeTool)) {
        setActiveTool('select');
      }
      // Disable zone creation when leaving zone overview
      if (onZoneCreateModeChange) {
        onZoneCreateModeChange(false);
      }
    }
  }, [useZones, zoneMode, activeTool, onZoneCreateModeChange]);

  // Resize handlers (cols + rows for stadium scale)
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({ x: e.clientX, y: e.clientY, gridCols, gridRows, axis: 'cols' });
  };

  const handleRowResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeStart({ x: e.clientX, y: e.clientY, gridCols, gridRows, axis: 'rows' });
  };

  const handleResizeMouseMove = (e) => {
    if (!isResizing || !resizeStart) return;

    if (resizeStart.axis === 'rows') {
      if (!setGridRows) return;
      const deltaY = e.clientY - resizeStart.y;
      const rowsChange = Math.round(deltaY / (GRID_SIZE * zoom));
      const newRows = Math.max(
        MIN_GRID_ROWS,
        Math.min(MAX_GRID_ROWS, resizeStart.gridRows + rowsChange),
      );
      if (newRows !== gridRows) setGridRows(newRows);
      return;
    }

    const deltaX = e.clientX - resizeStart.x;
    const colsChange = Math.round(deltaX / (GRID_SIZE * zoom));
    const newCols = Math.max(
      MIN_GRID_COLS,
      Math.min(MAX_GRID_COLS, resizeStart.gridCols + colsChange),
    );

    if (newCols !== gridCols) {
      setGridCols(newCols);
    }
  };

  const handleResizeMouseUp = () => {
    setIsResizing(false);
    setResizeStart(null);
  };

  // Tool-specific mouse handlers
  const canPanNow = () => activeTool === 'hand' || spacePan;

  const beginPan = (e) => {
    panActiveRef.current = true;
    panStartRef.current = { x: e.clientX, y: e.clientY };
    setIsPanning(true);
  };

  const endPan = () => {
    panActiveRef.current = false;
    panStartRef.current = null;
    setIsPanning(false);
  };

  const applyPanDelta = (e) => {
    const start = panStartRef.current;
    if (!panActiveRef.current || !start) return;
    const deltaX = e.clientX - start.x;
    const deltaY = e.clientY - start.y;
    setViewOffset((prev) => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY,
    }));
    panStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleToolMouseDown = (e) => {
    // Middle mouse always pans
    if (e.button === 1) {
      e.preventDefault();
      beginPan(e);
      return;
    }

    if (e.button !== 0) return;

    if (canPanNow()) {
      e.preventDefault();
      beginPan(e);
      return;
    }

    e.preventDefault();

    if (activeTool === 'zone-create' && useZones && zoneMode === 'zone-overview') {
      handleMouseDown(e);
      return;
    }

    if (activeTool === 'zoom') {
      const rect = gridRef.current?.getBoundingClientRect();
      if (rect) {
        if (e.shiftKey) {
          handleZoomClick && handleZoomClick(-0.2, e.clientX, e.clientY);
        } else {
          handleZoomClick && handleZoomClick(0.2, e.clientX, e.clientY);
        }
      }
      return;
    }

    if (positioningMode && positioningSeats && positioningSeats.length > 0) {
      onPositionGroup && onPositionGroup(e.clientX, e.clientY);
      return;
    }

    handleMouseDown(e);
  };

  const handleToolMouseMove = (e) => {
    if (panActiveRef.current) {
      e.preventDefault();
      applyPanDelta(e);
      return;
    }

    handleMouseMove(e);
  };

  const handleToolMouseUp = (e) => {
    if (panActiveRef.current) {
      endPan();
      return;
    }

    handleMouseUp(e);
  };

  // Keep panning even if cursor leaves the grid
  useEffect(() => {
    if (!isPanning) return undefined;
    const onMove = (e) => {
      e.preventDefault();
      applyPanDelta(e);
    };
    const onUp = () => endPan();
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [isPanning, setViewOffset]);

  // ESC / Space / Shift
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isFullscreen) {
        toggleFullscreen();
      }
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
      if (
        e.code === 'Space' &&
        !e.repeat &&
        !e.target?.closest?.('input,textarea,select,[contenteditable]')
      ) {
        e.preventDefault();
        if (activeTool !== 'hand') {
          previousToolRef.current = activeTool;
        }
        setSpacePan(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
      if (e.code === 'Space') {
        e.preventDefault();
        setSpacePan(false);
        endPan();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [isFullscreen, toggleFullscreen, activeTool, setActiveTool]);

  const handleViewportWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (handleWheel) handleWheel(e);
      return;
    }
    // Trackpad / mouse wheel = pan around the layout
    e.preventDefault();
    e.stopPropagation();
    setViewOffset((prev) => ({
      x: prev.x - e.deltaX,
      y: prev.y - e.deltaY,
    }));
  };

  const activateHandTool = () => {
    if (activeTool !== 'hand') previousToolRef.current = activeTool;
    setActiveTool('hand');
    setAddSeatMode && setAddSeatMode(false);
  };
  // Resize event listeners
  useEffect(() => {
    const handleMouseMove = (e) => {
      handleResizeMouseMove(e);
    };

    const handleMouseUp = () => {
      handleResizeMouseUp();
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStart, gridCols, gridRows, zoom]);

  return (
    <div
      className={`overflow-hidden w-full h-full flex ${
        isFullscreen ? 'bg-gray-900 rounded-none' : 'bg-white rounded-lg border border-gray-200'
      }`}
      ref={containerRef}
    >
      {/* Compact tool rail (optional — full tools live in left EditorSidebar) */}
      {!hideToolRail && (
        <div className="w-16 bg-gradient-to-b from-gray-800 to-gray-900 border-r border-gray-700 flex flex-col">
          {/* Logo/Brand */}
          <div className="h-16 flex items-center justify-center border-b border-gray-700">
            <div className="w-8 h-8 bg-[#AE256D] rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
            </div>
          </div>

          {/* Tools */}
          <div className="flex-1 py-4 space-y-2">
            {tools.map((tool) => (
              <div key={tool.id} className="relative group">
                <button
                  onClick={() => handleToolSelect(tool.id)}
                  className={`w-12 h-12 mx-2 rounded-lg flex items-center justify-center transition-all duration-200 ${
                    tool.active
                      ? 'bg-[#AE256D] text-white shadow-lg shadow-pink-500/25 ring-2 ring-pink-300'
                      : 'text-gray-400 hover:text-white hover:bg-gray-700'
                  }`}
                  title={tool.name}
                >
                  {tool.icon}
                </button>

                {/* Tooltip - Photoshop Style */}
                <div className="absolute left-16 top-1/2 transform -translate-y-1/2 invisible group-hover:visible z-50 ml-2">
                  <div className="bg-gray-900 text-white text-sm px-3 py-2 rounded-lg shadow-xl border border-gray-700 whitespace-nowrap">
                    <div className="font-medium">{tool.name}</div>
                    <div className="text-xs text-gray-300 mt-1">{tool.description}</div>

                    {/* Tooltip Arrow */}
                    <div className="absolute top-1/2 left-0 transform -translate-y-1/2 -translate-x-full">
                      <div className="border-4 border-transparent border-r-gray-900"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Canvas Area */}
      <div className="flex-1 flex flex-col">
        {/* Canvas Header with status */}
        <div
          className={`pl-2 pr-12 py-2 border-b border-gray-200 flex items-center justify-between ${
            isFullscreen ? 'bg-gradient-to-r from-gray-800 to-gray-900 text-white' : 'bg-gray-50'
          }`}
        >
          <div className="flex items-center space-x-3 flex-1 min-w-0 pr-3">
            <div
              className={`flex items-center space-x-2 text-xs ${isFullscreen ? 'text-gray-300' : 'text-gray-600'}`}
            >
              <span>
                Araç:{' '}
                <span
                  className={`font-medium ${isFullscreen ? 'text-pink-400' : 'text-[#AE256D]'}`}
                >
                  {tools.find((t) => t.id === activeTool)?.name || 'El Aracı'}
                </span>
              </span>
              {useZones && (
                <>
                  <span>•</span>
                  <span>
                    Mod:{' '}
                    <span
                      className={`font-medium ${
                        zoneMode === 'zone-overview'
                          ? isFullscreen
                            ? 'text-blue-400'
                            : 'text-blue-600'
                          : zoneMode === 'zone-edit'
                            ? isFullscreen
                              ? 'text-green-400'
                              : 'text-green-600'
                            : isFullscreen
                              ? 'text-gray-400'
                              : 'text-gray-600'
                      }`}
                    >
                      {zoneMode === 'zone-overview'
                        ? 'Zone Görünümü'
                        : zoneMode === 'zone-edit'
                          ? `Sayfa · ${selectedZone?.name || 'Zone'}`
                          : 'Koltuk'}
                    </span>
                  </span>
                </>
              )}
              <span>•</span>
              <span>Zoom: {Math.round(zoom * 100)}%</span>
              {useZones && (
                <>
                  <span>•</span>
                  <span>Zone: {zones.length}</span>
                </>
              )}
              <span>•</span>
              <span>
                Koltuk:{' '}
                {useZones && zoneMode === 'zone-edit' && selectedZone && !showAllZoneSeats
                  ? `${visibleSeats.length}/${seats.length}`
                  : seats.length}
              </span>
              {selectedSeats.length > 0 && (
                <>
                  <span>•</span>
                  <span>Seçili: {selectedSeats.length}</span>
                </>
              )}
            </div>

            {/* Zone Edit Mode — show all / exit */}
            {useZones && zoneMode === 'zone-edit' && (
              <div className="ml-2 flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => onToggleShowAllZoneSeats && onToggleShowAllZoneSeats()}
                  className={`px-2 py-1 rounded text-[10px] font-semibold transition-colors ${
                    showAllZoneSeats
                      ? isFullscreen
                        ? 'bg-pink-600 text-white'
                        : 'bg-[#AE256D] text-white'
                      : isFullscreen
                        ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={
                    showAllZoneSeats
                      ? 'Sadece bu zone’un koltuklarını göster'
                      : `Tüm koltukları göster${otherZoneSeatCount ? ` (+${otherZoneSeatCount} diğer)` : ''}`
                  }
                >
                  {showAllZoneSeats ? 'Sadece bu zone' : 'Tüm koltuklar'}
                </button>
                <button
                  onClick={() => {
                    if (typeof onZoneExit === 'function') {
                      onZoneExit();
                    }
                  }}
                  className={`p-1 rounded transition-colors ${
                    isFullscreen
                      ? 'text-gray-300 hover:text-red-400 hover:bg-gray-700'
                      : 'text-gray-600 hover:text-red-600 hover:bg-gray-100'
                  }`}
                  title="Zone Düzenlemeyi Kapat"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-4">
            {/* Hand / pan — navigate layout */}
            <button
              type="button"
              onClick={activateHandTool}
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                activeTool === 'hand' || spacePan
                  ? isFullscreen
                    ? 'bg-pink-600 text-white'
                    : 'bg-[#AE256D] text-white'
                  : isFullscreen
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              title="El aracı — sürükleyerek gez (Space basılı tut)"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M7 11V8a1 1 0 012 0v5m0-3V7a1 1 0 012 0v5m0-3.5V6a1 1 0 012 0v7m0-3a1 1 0 012 0v4.5a4.5 4.5 0 01-9 0V11"
                />
              </svg>
            </button>

            {/* Zoom Controls — navbar */}
            <div className="flex items-center gap-1 bg-black/20 rounded-lg px-1 py-0.5">
              <button
                type="button"
                onClick={zoomOut}
                className={`w-8 h-8 rounded-md text-lg font-bold leading-none transition-colors ${
                  isFullscreen ? 'text-white hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-200'
                }`}
                title="Uzaklaştır"
              >
                −
              </button>
              <button
                type="button"
                onClick={resetZoom}
                className={`min-w-[3.25rem] h-8 px-1 rounded-md text-xs font-mono tabular-nums transition-colors ${
                  isFullscreen
                    ? 'text-pink-300 hover:bg-gray-700'
                    : 'text-[#AE256D] hover:bg-gray-200'
                }`}
                title="100%"
              >
                {Math.round(zoom * 100)}%
              </button>
              <button
                type="button"
                onClick={zoomIn}
                className={`w-8 h-8 rounded-md text-lg font-bold leading-none transition-colors ${
                  isFullscreen ? 'text-white hover:bg-gray-700' : 'text-gray-800 hover:bg-gray-200'
                }`}
                title="Yakınlaştır"
              >
                +
              </button>
            </div>

            {/* Divider */}
            <div className={`h-6 w-px ${isFullscreen ? 'bg-gray-600' : 'bg-gray-300'}`}></div>

            {/* Undo/Redo - Compact */}
            <div className="flex items-center space-x-1">
              <button
                onClick={undo}
                disabled={historyIndex <= 0}
                className={`p-2 rounded-lg transition-all ${
                  historyIndex <= 0
                    ? isFullscreen
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : isFullscreen
                      ? 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-pink-400 hover:text-pink-400'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-[#AE256D] hover:text-[#AE256D]'
                }`}
                title="Geri Al (Cmd+Z)"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                  />
                </svg>
              </button>
              <button
                onClick={redo}
                disabled={historyIndex >= history.length - 1}
                className={`p-2 rounded-lg transition-all ${
                  historyIndex >= history.length - 1
                    ? isFullscreen
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : isFullscreen
                      ? 'bg-gray-700 border border-gray-600 text-gray-300 hover:bg-gray-600 hover:border-pink-400 hover:text-pink-400'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-[#AE256D] hover:text-[#AE256D]'
                }`}
                title="İleri Al (Cmd+Y)"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 10h-10a8 8 0 00-8 8v2m18-10l-6-6m6 6l-6 6"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Stage */}
        <div className="text-center py-3 bg-gray-800">
          <div className="inline-block text-white px-6 py-2 font-bold tracking-wider text-sm">
            SAHNE
          </div>
        </div>

        {/* Grid Container */}
        <div
          className={`relative overflow-hidden bg-gray-50 w-full ${
            isFullscreen ? 'h-[calc(100vh-100px)]' : 'h-[calc(100vh-250px)] min-h-[700px]'
          }`}
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            cursor:
              isPanning || activeTool === 'hand' || spacePan
                ? isPanning
                  ? 'grabbing'
                  : 'grab'
                : undefined,
          }}
          onMouseDown={(e) => {
            // Pan when clicking empty viewport chrome (not only the grid)
            if (e.button === 1 || ((activeTool === 'hand' || spacePan) && e.button === 0)) {
              if (e.target === e.currentTarget) {
                e.preventDefault();
                beginPan(e);
              }
            }
          }}
          onWheel={handleViewportWheel}
        >
          <div
            ref={gridRef}
            className={`relative select-none my-4 ml-8 ${
              positioningMode ? 'cursor-move' : addSeatMode ? 'cursor-crosshair' : 'cursor-default'
            }`}
            style={{
              width: `${gridCols * GRID_SIZE}px`,
              height: `${gridRows * GRID_SIZE}px`,
              backgroundImage:
                useZones && zoneMode === 'zone-overview'
                  ? 'none'
                  : `
              linear-gradient(to right, rgba(209,213,219,${backgroundImage ? 0.35 : 1}) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(209,213,219,${backgroundImage ? 0.35 : 1}) 1px, transparent 1px)
            `,
              backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
              backgroundColor:
                useZones && zoneMode === 'zone-overview'
                  ? '#e8eef3'
                  : backgroundImage
                    ? 'transparent'
                    : useZones && zoneMode === 'zone-edit' && selectedZone?.color
                      ? `${selectedZone.color}12`
                      : 'white',
              border: '2px solid #d1d5db',
              transform: `translate(${viewOffset.x}px, ${viewOffset.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              willChange: 'transform',
              cursor:
                isPanning || activeTool === 'hand' || spacePan
                  ? isPanning
                    ? 'grabbing'
                    : 'grab'
                  : positioningMode
                    ? 'move'
                    : seatTool === 'paint' || activeTool === 'add'
                      ? 'cell'
                      : seatTool === 'erase'
                        ? 'not-allowed'
                        : seatTool === 'box'
                          ? 'crosshair'
                          : activeTool === 'zoom'
                            ? isShiftPressed
                              ? 'zoom-out'
                              : 'zoom-in'
                            : useZones && zoneMode === 'zone-overview' && isCreatingZone
                              ? 'crosshair'
                              : 'default',
            }}
            onMouseDown={handleToolMouseDown}
            onMouseMove={handleToolMouseMove}
            onMouseUp={handleToolMouseUp}
            onContextMenu={handleContextMenu}
            onAuxClick={(e) => {
              // Prevent middle-click autoscroll
              if (e.button === 1) e.preventDefault();
            }}
            onMouseLeave={() => {
              // Handle mouse leave events if needed
            }}
          >
            {/* Floor plan / stadium image under seats */}
            {backgroundImage && (
              <img
                src={backgroundImage}
                alt=""
                draggable={false}
                className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
                style={{ opacity: backgroundOpacity, zIndex: 0 }}
              />
            )}

            {/* Aisle / void columns */}
            {naming &&
              [...(naming.aisleCols || []), ...(naming.voidCols || [])].map((col) => (
                <div
                  key={`aisle-${col}`}
                  className="absolute top-0 bottom-0 pointer-events-none"
                  style={{
                    left: col * GRID_SIZE,
                    width: GRID_SIZE,
                    height: '100%',
                    background: naming.voidCols?.includes(col)
                      ? 'repeating-linear-gradient(45deg, rgba(239,68,68,0.15), rgba(239,68,68,0.15) 4px, rgba(239,68,68,0.05) 4px, rgba(239,68,68,0.05) 8px)'
                      : 'repeating-linear-gradient(45deg, rgba(245,158,11,0.2), rgba(245,158,11,0.2) 4px, rgba(245,158,11,0.06) 4px, rgba(245,158,11,0.06) 8px)',
                    zIndex: 1,
                  }}
                  title={
                    naming.voidCols?.includes(col)
                      ? `Kolon ${col + 1} geçersiz`
                      : `Kolon ${col + 1} koridor (boş)`
                  }
                />
              ))}

            {/* Per-cell corridors (selected seats emptied — not whole column) */}
            {naming &&
              (naming.aisleCells || []).map((key) => {
                const [r, c] = String(key).split('-').map(Number);
                if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
                return (
                  <div
                    key={`aisle-cell-${key}`}
                    className="absolute pointer-events-none rounded-sm"
                    style={{
                      left: c * GRID_SIZE + 2,
                      top: r * GRID_SIZE + 2,
                      width: GRID_SIZE - 4,
                      height: GRID_SIZE - 4,
                      background:
                        'repeating-linear-gradient(45deg, rgba(245,158,11,0.35), rgba(245,158,11,0.35) 3px, rgba(245,158,11,0.1) 3px, rgba(245,158,11,0.1) 6px)',
                      zIndex: 2,
                    }}
                    title={`Koridor ${r}-${c}`}
                  />
                );
              })}

            {/* Void rows */}
            {naming &&
              (naming.voidRows || []).map((row) => (
                <div
                  key={`void-row-${row}`}
                  className="absolute left-0 right-0 pointer-events-none"
                  style={{
                    top: row * GRID_SIZE,
                    height: GRID_SIZE,
                    width: '100%',
                    background:
                      'repeating-linear-gradient(45deg, rgba(239,68,68,0.12), rgba(239,68,68,0.12) 4px, rgba(239,68,68,0.04) 4px, rgba(239,68,68,0.04) 8px)',
                    zIndex: 1,
                  }}
                  title={`Satır ${row + 1} geçersiz`}
                />
              ))}

            {/* Hover ghost — where the next seat will land */}
            {hoverCell && (seatTool === 'paint' || seatTool === 'erase') && !isSelecting && (
              <div
                className={`absolute pointer-events-none rounded-sm border-2 ${
                  seatTool === 'erase'
                    ? 'border-red-500 bg-red-400/30'
                    : 'border-[#AE256D] bg-[#AE256D]/35'
                }`}
                style={{
                  left: hoverCell.col * GRID_SIZE + 2,
                  top: hoverCell.row * GRID_SIZE + 2,
                  width: GRID_SIZE - 4,
                  height: GRID_SIZE - 4,
                  zIndex: 20,
                }}
              />
            )}

            {/* Box-fill rubber band */}
            {boxStart && boxEnd && seatTool === 'box' && (
              <div
                className="absolute pointer-events-none border-2 border-dashed border-indigo-500 bg-indigo-400/20"
                style={{
                  left: Math.min(boxStart.col, boxEnd.col) * GRID_SIZE,
                  top: Math.min(boxStart.row, boxEnd.row) * GRID_SIZE,
                  width: (Math.abs(boxEnd.col - boxStart.col) + 1) * GRID_SIZE,
                  height: (Math.abs(boxEnd.row - boxStart.row) + 1) * GRID_SIZE,
                  zIndex: 20,
                }}
              />
            )}

            {/* Selection Box */}
            {isSelecting && selectionBox && (
              <div
                className="absolute border-2 pointer-events-none border-blue-500 bg-blue-200 bg-opacity-30"
                style={{
                  left: selectionBox.minCol * GRID_SIZE,
                  top: selectionBox.minRow * GRID_SIZE,
                  width: (selectionBox.maxCol - selectionBox.minCol + 1) * GRID_SIZE,
                  height: (selectionBox.maxRow - selectionBox.minRow + 1) * GRID_SIZE,
                }}
              />
            )}

            {/* Empty Area Selection Box (persistent) */}
            {emptyAreaSelection && !isSelecting && (
              <>
                <div
                  className="absolute border-2 border-green-500 bg-green-200 bg-opacity-20 pointer-events-none"
                  style={{
                    left: emptyAreaSelection.minCol * GRID_SIZE,
                    top: emptyAreaSelection.minRow * GRID_SIZE,
                    width: (emptyAreaSelection.maxCol - emptyAreaSelection.minCol + 1) * GRID_SIZE,
                    height: (emptyAreaSelection.maxRow - emptyAreaSelection.minRow + 1) * GRID_SIZE,
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="bg-green-600 text-white px-1 py-0.5 rounded shadow max-w-full overflow-hidden text-xs"
                      style={{
                        lineHeight: '1.2',
                      }}
                    >
                      Boş Alan
                    </div>
                  </div>
                </div>

                {/* Empty Area Selection Dropdown - Context Menu Style */}
                <div
                  className="absolute bg-white border border-gray-200 rounded-lg shadow-lg py-2 pointer-events-auto z-50"
                  style={{
                    left: emptyAreaSelection.minCol * GRID_SIZE,
                    top: Math.max(0, emptyAreaSelection.minRow * GRID_SIZE - 90),
                    minWidth: '200px',
                  }}
                >
                  <button
                    onClick={() => {
                      fillEmptyAreaWithSeats && fillEmptyAreaWithSeats(emptyAreaSelection);
                    }}
                    className="w-full px-4 py-2 text-left text-green-600 hover:bg-green-50 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                      />
                    </svg>
                    <span>
                      Seçili Alanı Koltukla Doldur (
                      {(emptyAreaSelection.maxRow - emptyAreaSelection.minRow + 1) *
                        (emptyAreaSelection.maxCol - emptyAreaSelection.minCol + 1)}{' '}
                      hücre)
                    </span>
                  </button>
                  <button
                    onClick={() => setEmptyAreaSelection && setEmptyAreaSelection(null)}
                    className="w-full px-4 py-2 text-left text-gray-600 hover:bg-gray-50 flex items-center space-x-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    <span>Seçimi Temizle</span>
                  </button>
                </div>
              </>
            )}

            {/* Zones — only on overview; inside a zone the outline is hidden (seat page) */}
            {useZones && zoneMode === 'zone-overview' && (
              <svg
                className="absolute inset-0"
                width="100%"
                height="100%"
                style={{
                  zIndex: 3,
                  overflow: 'visible',
                  pointerEvents: 'auto',
                }}
              >
                {zones.map((zone) => {
                  const selected = highlightedZone?.id === zone.id || selectedZone?.id === zone.id;
                  const isStage = !!zone.isStage || zone.name === 'SAHNE';
                  const poly = zone.type === 'POLYGON' ? zone.polygonPoints || zone.points : null;

                  if (poly && poly.length >= 3) {
                    const d =
                      poly.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
                    const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
                    const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
                    return (
                      <g key={zone.id}>
                        <path
                          d={d}
                          fill={zone.color}
                          fillOpacity={selected ? 0.9 : isStage ? 0.55 : 0.82}
                          stroke={selected ? '#ffffff' : 'rgba(0,0,0,0.4)'}
                          strokeWidth={selected ? 4 : 2.5}
                          style={{
                            cursor: isStage ? 'default' : isDraggingZone ? 'grabbing' : 'grab',
                            filter: selected
                              ? 'drop-shadow(0 0 10px rgba(255,255,255,0.5))'
                              : 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onZoneClick && onZoneClick(zone, e.clientX, e.clientY);
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            onZoneDoubleClick && onZoneDoubleClick(zone);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            onZoneMouseDown && onZoneMouseDown(zone, e);
                          }}
                        />
                        <text
                          x={cx}
                          y={cy}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#fff"
                          fontWeight="700"
                          fontSize={Math.max(11, Math.min(18, (zone.width || 80) / 6))}
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                          paintOrder="stroke"
                          stroke="rgba(0,0,0,0.5)"
                          strokeWidth="3"
                        >
                          {zone.name}
                        </text>
                      </g>
                    );
                  }

                  const x = zone.x || 0;
                  const y = zone.y || 0;
                  const w = zone.width || 100;
                  const h = zone.isCircle ? w : zone.height || 80;
                  return (
                    <g key={zone.id}>
                      {zone.isCircle ? (
                        <ellipse
                          cx={x + w / 2}
                          cy={y + h / 2}
                          rx={w / 2}
                          ry={h / 2}
                          fill={zone.color}
                          fillOpacity={selected ? 0.9 : 0.82}
                          stroke={selected ? '#fff' : 'rgba(0,0,0,0.4)'}
                          strokeWidth={selected ? 4 : 2.5}
                          style={{
                            cursor: isStage ? 'default' : 'grab',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onZoneClick && onZoneClick(zone, e.clientX, e.clientY);
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            onZoneDoubleClick && onZoneDoubleClick(zone);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            onZoneMouseDown && onZoneMouseDown(zone, e);
                          }}
                        />
                      ) : (
                        <rect
                          x={x}
                          y={y}
                          width={w}
                          height={h}
                          rx={isStage ? 8 : 6}
                          fill={zone.color}
                          fillOpacity={selected ? 0.9 : isStage ? 0.55 : 0.82}
                          stroke={selected ? '#fff' : 'rgba(0,0,0,0.4)'}
                          strokeWidth={selected ? 4 : 2.5}
                          style={{
                            cursor: isStage ? 'default' : 'grab',
                            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onZoneClick && onZoneClick(zone, e.clientX, e.clientY);
                          }}
                          onDoubleClick={(e) => {
                            e.stopPropagation();
                            onZoneDoubleClick && onZoneDoubleClick(zone);
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            onZoneMouseDown && onZoneMouseDown(zone, e);
                          }}
                        />
                      )}
                      <text
                        x={x + w / 2}
                        y={y + h / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#fff"
                        fontWeight="700"
                        fontSize={Math.max(12, Math.min(20, w / 8))}
                        style={{ pointerEvents: 'none', userSelect: 'none' }}
                        paintOrder="stroke"
                        stroke="rgba(0,0,0,0.5)"
                        strokeWidth="3"
                      >
                        {zone.name}
                      </text>
                    </g>
                  );
                })}
              </svg>
            )}

            {/* Preview Zone (while drawing) */}
            {useZones && isDrawingZone && previewZone && (
              <>
                {zoneDrawingMode === 'rectangle' && (
                  <div
                    className="absolute border-2 border-dashed rounded-lg pointer-events-none"
                    style={{
                      left: previewZone.x,
                      top: previewZone.y,
                      width: previewZone.width,
                      height: previewZone.height,
                      backgroundColor: previewZone.color + '30',
                      borderColor: previewZone.color,
                      zIndex: 10,
                    }}
                  >
                    <div
                      className="absolute inset-0 flex items-center justify-center text-white font-bold rounded-lg"
                      style={{
                        backgroundColor: previewZone.color + '60',
                        fontSize: '14px',
                      }}
                    >
                      {previewZone.name || 'Yeni Zone'}
                    </div>
                  </div>
                )}

                {zoneDrawingMode === 'circle' && (
                  <div
                    className="absolute border-2 border-dashed rounded-full pointer-events-none"
                    style={{
                      left: previewZone.x,
                      top: previewZone.y,
                      width: previewZone.width,
                      height: previewZone.width,
                      backgroundColor: previewZone.color + '30',
                      borderColor: previewZone.color,
                      zIndex: 10,
                    }}
                  >
                    <div
                      className="absolute inset-0 flex items-center justify-center text-white font-bold rounded-full"
                      style={{
                        backgroundColor: previewZone.color + '60',
                        fontSize: '14px',
                      }}
                    >
                      {previewZone.name || 'Yeni Zone'}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Polygon Preview Points — coords are content-space; parent already has scale(zoom) */}
            {useZones && zoneDrawingMode === 'polygon' && polygonPoints.length > 0 && (
              <svg
                className="absolute inset-0 pointer-events-none"
                width="100%"
                height="100%"
                style={{ zIndex: 12, overflow: 'visible' }}
              >
                {polygonPoints.length >= 2 && (
                  <polyline
                    points={polygonPoints.map((p) => `${p.x},${p.y}`).join(' ')}
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeDasharray="6 4"
                  />
                )}
                {polygonPoints.length >= 3 && (
                  <line
                    x1={polygonPoints[polygonPoints.length - 1].x}
                    y1={polygonPoints[polygonPoints.length - 1].y}
                    x2={polygonPoints[0].x}
                    y2={polygonPoints[0].y}
                    stroke="#93C5FD"
                    strokeWidth="1.5"
                    strokeDasharray="4 4"
                  />
                )}
                {polygonPoints.map((point, index) => (
                  <g key={`point-${index}`}>
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r={index === 0 ? 7 : 5}
                      fill={index === 0 ? '#2563EB' : '#3B82F6'}
                      stroke="#fff"
                      strokeWidth="2"
                    />
                    {index === 0 && (
                      <text
                        x={point.x}
                        y={point.y - 12}
                        textAnchor="middle"
                        fill="#1D4ED8"
                        fontSize="10"
                        fontWeight="700"
                      >
                        başlangıç
                      </text>
                    )}
                  </g>
                ))}
              </svg>
            )}

            {/* Row and Column Labels — right-click to void / aisle */}
            {!(useZones && zoneMode === 'zone-overview') && (
              <>
                {Array.from({ length: gridCols }, (_, i) => {
                  const skipped = naming && isSkippedCol(i, naming);
                  const display =
                    naming && !skipped ? getSeatNumberForCol(i, naming) : skipped ? '·' : i + 1;
                  return (
                    <div
                      key={`col-${i}`}
                      className={`absolute text-xs font-mono select-none cursor-context-menu px-0.5 rounded ${
                        skipped
                          ? 'text-amber-600 bg-amber-100/80'
                          : 'text-gray-600 hover:bg-pink-100 hover:text-[#AE256D]'
                      }`}
                      style={{
                        left: i * GRID_SIZE + GRID_SIZE / 2 - 8,
                        top: -22,
                        zIndex: 20,
                        pointerEvents: 'auto',
                      }}
                      title={`Kolon ${i + 1} — sağ tık menü`}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onAxisContextMenu &&
                          onAxisContextMenu({
                            kind: 'col',
                            index: i,
                            clientX: e.clientX,
                            clientY: e.clientY,
                          });
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {display}
                    </div>
                  );
                })}

                {Array.from({ length: gridRows }, (_, i) => {
                  const skipped = naming && isSkippedRow(i, naming);
                  const display = naming ? getRowHeaderLabel(i, naming) : rowIndexToLabel(i);
                  return (
                    <div
                      key={`row-${i}`}
                      className={`absolute text-xs font-mono select-none cursor-context-menu px-0.5 rounded ${
                        skipped
                          ? 'text-red-600 bg-red-100/80'
                          : 'text-gray-600 hover:bg-pink-100 hover:text-[#AE256D]'
                      }`}
                      style={{
                        left: -28,
                        top: i * GRID_SIZE + GRID_SIZE / 2 - 7,
                        zIndex: 20,
                        pointerEvents: 'auto',
                      }}
                      title={`Satır ${display} — sağ tık menü`}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onAxisContextMenu &&
                          onAxisContextMenu({
                            kind: 'row',
                            index: i,
                            clientX: e.clientX,
                            clientY: e.clientY,
                          });
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {display}
                    </div>
                  );
                })}
              </>
            )}

            {/* Seats - Only show in seat design and zone edit modes */}
            {!(useZones && zoneMode === 'zone-overview') &&
              visibleSeats.map((seat) => {
                const seatTypeInfo = getSeatTypeInfo(seat.seatType);
                // Yersizleştirilmiş koltuklar için özel key hesaplama
                const seatKey = seat.isDetached
                  ? `${seat.row}-${seat.col}`
                  : `${Math.round(seat.row)}-${Math.round(seat.col)}`;
                const isSelected = selectedSeats.includes(seatKey);
                const isPositioning = positioningSeats.includes(seatKey);
                const isPrecisionMode = seat.isDetached; // Yersizleştirilmiş koltuklar

                // Find zone for this seat if using zones
                const seatZone =
                  useZones && seat.zoneId ? zones.find((z) => z.id === seat.zoneId) : null;
                const isOtherZone =
                  useZones &&
                  zoneMode === 'zone-edit' &&
                  showAllZoneSeats &&
                  selectedZone &&
                  seat.zoneId !== selectedZone.id;

                return (
                  <div
                    key={seat.id}
                    className={`absolute rounded-lg transition-all duration-200 flex items-center justify-center text-white text-xs font-bold border-2 ${
                      isPrecisionMode
                        ? 'z-30'
                        : isPositioning
                          ? 'ring-4 ring-red-400 z-20 scale-110 animate-pulse'
                          : isSelected
                            ? 'ring-4 ring-purple-300 z-10 scale-110'
                            : 'hover:scale-105'
                    }`}
                    style={{
                      left: seat.col * GRID_SIZE + 2,
                      top: seat.row * GRID_SIZE + 2,
                      width: GRID_SIZE - 4,
                      height: GRID_SIZE - 4,
                      backgroundColor: seatZone?.color || seatTypeInfo.color,
                      borderColor: isPrecisionMode
                        ? '#EF4444'
                        : isPositioning
                          ? '#EF4444'
                          : isSelected
                            ? '#8B5CF6'
                            : seatZone?.color || seatTypeInfo.color,
                      fontSize: '10px',
                      zIndex: isOtherZone ? 5 : 10,
                      opacity: isOtherZone ? 0.35 : 1,
                      pointerEvents: isOtherZone ? 'none' : 'auto',
                    }}
                    title={`Koltuk ${seat.label}${seatZone ? ` (${seatZone.name})` : ''}${
                      isOtherZone
                        ? ' — diğer zone'
                        : isPrecisionMode
                          ? ' (Yersizleştirilmiş)'
                          : isPositioning
                            ? ' (Yerleştirme Modu)'
                            : ''
                    }`}
                  >
                    <div className="text-center">
                      <div className="font-bold text-white">
                        {displaySeatLabel(seat, naming || undefined)}
                        {seat.isPrecisionPositioned && '*'}
                      </div>
                    </div>
                  </div>
                );
              })}

            {/* Ghost Preview Seats - Positioning Mode - Only show in seat design and zone edit modes */}
            {!(useZones && zoneMode === 'zone-overview') &&
              positioningMode &&
              ghostPreviewSeats &&
              ghostPreviewSeats.length > 0 && (
                <>
                  {ghostPreviewSeats.map((ghostSeat, index) => {
                    const seatTypeInfo = getSeatTypeInfo(ghostSeat.seatType);

                    return (
                      <div
                        key={`ghost-${index}-${ghostSeat.row}-${ghostSeat.col}`}
                        className="absolute rounded-lg border-2 border-dashed border-green-500 bg-green-200 bg-opacity-60 flex items-center justify-center text-green-800 text-xs font-bold shadow-lg"
                        style={{
                          left: ghostSeat.col * GRID_SIZE + 2,
                          top: ghostSeat.row * GRID_SIZE + 2,
                          width: GRID_SIZE - 4,
                          height: GRID_SIZE - 4,
                          fontSize: '10px',
                          zIndex: 15,
                        }}
                        title={`Hedef: ${
                          ghostSeat.label ||
                          `${String.fromCharCode(65 + ghostSeat.row)}${
                            naming ? getSeatNumberForCol(ghostSeat.col, naming) : ghostSeat.col + 1
                          }`
                        }`}
                      >
                        <div className="text-center">
                          <div className="font-bold">
                            {ghostSeat.label ||
                              `${String.fromCharCode(65 + ghostSeat.row)}${
                                naming
                                  ? getSeatNumberForCol(ghostSeat.col, naming)
                                  : ghostSeat.col + 1
                              }`}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

            {/* Grid Resize Handles — stadium scale */}
            <div
              className="absolute top-0 w-2 bg-transparent hover:bg-blue-200 cursor-col-resize opacity-50 hover:opacity-100 transition-all"
              style={{
                left: `${gridCols * GRID_SIZE}px`,
                height: `${gridRows * GRID_SIZE}px`,
              }}
              onMouseDown={handleResizeMouseDown}
              title={`Grid genişliği: ${gridCols} kolon (max ${MAX_GRID_COLS}) • Sürükleyerek büyüt`}
            >
              <div className="absolute inset-y-0 right-0 w-0.5 bg-blue-500 opacity-0 hover:opacity-100"></div>
            </div>
            <div
              className="absolute left-0 h-2 bg-transparent hover:bg-blue-200 cursor-row-resize opacity-50 hover:opacity-100 transition-all"
              style={{
                top: `${gridRows * GRID_SIZE}px`,
                width: `${gridCols * GRID_SIZE}px`,
              }}
              onMouseDown={handleRowResizeMouseDown}
              title={`Grid yüksekliği: ${gridRows} satır (max ${MAX_GRID_ROWS}) • Sürükleyerek büyüt`}
            >
              <div className="absolute inset-x-0 bottom-0 h-0.5 bg-blue-500 opacity-0 hover:opacity-100"></div>
            </div>
            <div
              className="absolute pointer-events-none text-[10px] text-blue-300 bg-gray-900/80 px-2 py-0.5 rounded"
              style={{ left: 8, top: 8 }}
            >
              {gridCols}×{gridRows} · ~{gridCols * gridRows} hücre
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
